import {
    Alert,
    AlertIcon,
    Box,
    Button,
    Code,
    Text,
    useClipboard,
    VStack,
} from '@chakra-ui/react';
import { useState } from 'react';
import FormField from '../../../components/fields/FormField';
import FormModal from '../../../components/FormModal';
import { translate } from '../../../i18n';
import { ProvisionCoachAccessOutputDto } from '../../../models/coach/dto/provision-coach-access.dto';
import { useProvisionCoachAccess } from '../hooks/useProvisionCoachAccess';

type Props = {
    coachId: string;
    isOpen: boolean;
    onClose: () => void;
    organizationId?: string;
};

/**
 * Operator-driven onboarding: give an existing coach profile a dashboard
 * login without going through the email invite. On success the modal shows
 * the ONE-TIME password-setup link (fresh accounts) — it is not retrievable
 * later, so it stays on screen until the admin closes deliberately.
 */
export default function ProvisionCoachAccessModal({ coachId, isOpen, onClose, organizationId }: Props) {
    const [email, setEmail] = useState('');
    const [result, setResult] = useState<ProvisionCoachAccessOutputDto | null>(null);
    const provisionMutation = useProvisionCoachAccess(coachId, organizationId);
    const { onCopy, hasCopied } = useClipboard(result?.passwordSetupLink ?? '');

    const closeAndReset = () => {
        setEmail('');
        setResult(null);
        provisionMutation.reset();
        onClose();
    };

    const handleSubmit = () => {
        if (result) {
            closeAndReset();
            return;
        }
        // Errors surface through the global mutation-cache toast (409 messages
        // from the API are specific: already linked, email taken, …).
        provisionMutation.mutate(
            { email: email.trim() },
            { onSuccess: (data) => setResult(data) },
        );
    };

    const emailLooksValid = /.+@.+\..+/.test(email.trim());

    return (
        <FormModal
            isOpen={isOpen}
            onClose={closeAndReset}
            title={translate('coachs.provision_access_title')}
            onSubmit={handleSubmit}
            submitLabel={result ? translate('close') : translate('submit')}
            cancelLabel={translate('cancel')}
            isSubmitting={provisionMutation.isLoading}
            isSubmitDisabled={!result && !emailLooksValid}
            size="md"
        >
            {!result ? (
                <VStack align="stretch" spacing={4}>
                    <Text fontSize="sm" color="gray.500">
                        {translate('coachs.provision_access_description')}
                    </Text>
                    <FormField
                        type="email"
                        label={translate('coachs.email')}
                        value={email}
                        onChange={(value: string) => setEmail(value)}
                    />
                </VStack>
            ) : (
                <VStack align="stretch" spacing={4}>
                    {result.passwordSetupLink ? (
                        <>
                            <Alert status="success" borderRadius="md">
                                <AlertIcon />
                                {translate('coachs.provision_access_success_new')}
                            </Alert>
                            <Box>
                                <Code p={2} borderRadius="md" fontSize="xs" wordBreak="break-all" display="block">
                                    {result.passwordSetupLink}
                                </Code>
                                <Button mt={2} size="sm" colorScheme="brand" variant="outline" onClick={onCopy}>
                                    {hasCopied
                                        ? translate('coachs.provision_access_copied')
                                        : translate('coachs.provision_access_copy_link')}
                                </Button>
                            </Box>
                            <Text fontSize="xs" color="gray.500">
                                {translate('coachs.provision_access_one_time_note')}
                            </Text>
                        </>
                    ) : (
                        <Alert status={result.existingAccount ? 'info' : 'warning'} borderRadius="md">
                            <AlertIcon />
                            {result.existingAccount
                                ? translate('coachs.provision_access_success_existing')
                                : translate('coachs.provision_access_link_failed')}
                        </Alert>
                    )}
                </VStack>
            )}
        </FormModal>
    );
}
